import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { SuiToastService } from '@semeris/ui';
import { isEqual, uniq } from 'lodash-es';
import { EMPTY, merge, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  pairwise,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { UserProfileActions } from './user-profile.actions';
import { UserProfileSelectors } from './user-profile.selectors';
import { recordRecentWorkspace } from '../../features/home/models/recent-workspaces';
import { UserProfileService } from '../../features/settings/services/user-profile.service';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { EmailVerificationService } from '../../features/shared/services/email-verification.service';
import { PermissionService } from '../../features/shared/services/permission.service';
import { createWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { resolveTabScope } from '../../features/shared/utils/tab-workspace-storage';
import { wsLink } from '../../features/shared/utils/workspace-link';
import { AppActions } from '../app/app.actions';
import { AppSelectors } from '../app/app.selectors';
import { RootState } from '../root-state.model';

/** The store holds `null` for "not impersonating"; a profile field can arrive `undefined`. */
const hasImpersonationChanged = (before?: string | null, after?: string | null): boolean => (before ?? null) !== (after ?? null);

@Injectable()
export class UserProfileEffects {
  private readonly service = inject(UserProfileService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly toastService = inject(SuiToastService);
  private readonly permissionService = inject(PermissionService);
  private readonly emailVerification = inject(EmailVerificationService);

  wsEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.AssetSetting,
    () => UserProfileActions.RefreshAssetSetting(),
  );

  refreshAssetSettingsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.RefreshAssetSetting),
      switchMap(() =>
        this.service.refreshAssetSettings().pipe(
          map((assetSetting) => UserProfileActions.RefreshAssetSettingSuccess({ assetSetting })),
          reportEffectError(UserProfileActions.RefreshAssetSettingFail),
        ),
      ),
    ),
  );

  loadProfileEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.LoadProfile),
      mergeMap((action) => {
        console.debug(`[UserProfileActions.LoadProfile] received`);
        return this.service.getProfile().pipe(
          tap(() => {
            console.debug(`[UserProfileActions.LoadProfile] profile fetched`);
          }),
          withLatestFrom(this.store.select(AppSelectors.resolvedWorkspaceScope)),
          map(([profile, storeScope]) => {
            console.debug(`[UserProfileActions.LoadProfile] dispatching [UserProfileActions.LoadProfileSuccess]`);
            // The tab's own claim wins over the stored default — the boot request was already scoped
            // to it by `pre-fetch.js`, so seeding the store from the profile would leave the store
            // describing one workspace while the data on screen came from another.
            // Store first: on a mid-session reload (Settings) it is the tab's scope even with no sessionStorage.
            const claimed = storeScope ?? resolveTabScope();

            return UserProfileActions.LoadProfileSuccess({
              profile,
              init: action.init,
              selectedAssetShortName: claimed?.assets ?? profile.assetShortNames ?? profile.availableAssetShortNames,
              claimedWorkspaceId: claimed?.workspaceId,
            });
          }),
          reportEffectError(UserProfileActions.LoadProfileFail),
        );
      }),
    ),
  );

  updateProfileEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateProfile),
      withLatestFrom(this.store.select(UserProfileSelectors.impersonatedTenant), this.store.select(UserProfileSelectors.impersonatedUser)),
      switchMap(([action, impersonatedTenant, impersonatedUser]) =>
        this.service.updateProfile(action.profile).pipe(
          map((profile) =>
            UserProfileActions.UpdateProfileSuccess({
              profile,
              reloadApp:
                hasImpersonationChanged(impersonatedTenant, action.profile.impersonatedTenant) ||
                hasImpersonationChanged(impersonatedUser, action.profile.impersonatedUser),
            }),
          ),
          reportEffectError(UserProfileActions.UpdateProfileFail),
        ),
      ),
    ),
  );

  updateAssetSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateAssetSetting),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service.updateAssetSettings(action.setting).pipe(
          map(() => UserProfileActions.UpdateAssetSettingSuccess()),
          reportEffectError(UserProfileActions.UpdateAssetSettingFail),
        ),
      ),
    ),
  );

  /** The reducer merges asset settings optimistically, so a rejected write must resync from the server. */
  resyncAssetSettingAfterFailedWrite$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateAssetSettingFail),
      map(() => UserProfileActions.RefreshAssetSetting()),
    ),
  );

  updateDatapointCompareSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SetDatapointCompareSettings),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service
          .updateUserSettings({
            datapointCompareSettings: {
              showCategories: action.datapointCompareSettings?.showCategories,
              showDetails: action.datapointCompareSettings?.showDetails,
              sort: {
                active: action.datapointCompareSettings?.sort?.active,
                direction: action.datapointCompareSettings?.sort?.direction,
              },
            },
          })
          .pipe(
            map(() =>
              UserProfileActions.SetDatapointCompareSettingsSuccess({
                datapointCompareSettings: action.datapointCompareSettings,
              }),
            ),
            reportEffectError(UserProfileActions.SetDatapointCompareSettingsFail),
          ),
      ),
    ),
  );

  updateDealDockLayouts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SetDealDockLayouts),
      concatMap((action) =>
        this.service
          .updateDealDockLayouts(
            action.dealDockLayouts.map((l) => ({ name: l.name, layout: l.layout })),
            action.workspaceId,
          )
          .pipe(
            map(() => UserProfileActions.SetDealDockLayoutsSuccess()),
            reportEffectError(UserProfileActions.SetDealDockLayoutsFail),
          ),
      ),
    ),
  );

  /** The reducer applies the new list optimistically, so a rejected save would otherwise look like it worked. */
  setDealDockLayoutsFailEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.SetDealDockLayoutsFail),
        tap(() => this.toastService.error('Failed to save the layout — please try again')),
      ),
    { dispatch: false },
  );

  updateAssetSettingRecord$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateAssetSettingRecord),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service.updateAssetSettingRecord(action.record).pipe(
          map(() => UserProfileActions.UpdateAssetSettingRecordSuccess()),
          reportEffectError(UserProfileActions.UpdateAssetSettingRecordFail),
        ),
      ),
    ),
  );

  unlockEntity$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UnlockEntity),
      withLatestFrom(this.store),
      concatMap(([action, store]) =>
        this.service.unlockEntity(action.entityId).pipe(
          map((unlockedEntities) =>
            UserProfileActions.UnlockEntitySuccess({
              unlockedEntities,
            }),
          ),
          reportEffectError(UserProfileActions.UnlockEntityFail),
        ),
      ),
    ),
  );

  updateCompletedTours$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.ToggleCompletedTours),
      withLatestFrom(this.store),
      concatMap(([action, store]) => {
        const list = [...(store.userProfile.completedTourIds || [])];

        if (!action.completed && list.includes(action.tourId)) {
          list.splice(list.indexOf(action.tourId), 1);
        } else if (action.completed) {
          list.push(action.tourId);
        }

        return this.service.updateCompletedTourList(uniq(list)).pipe(
          map((tourIds) =>
            UserProfileActions.ToggleCompletedToursSuccess({
              tourIds,
            }),
          ),
          reportEffectError(UserProfileActions.ToggleCompletedToursFail),
        );
      }),
    ),
  );

  updateMultipleAssetSetting$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateMultipleAssetSetting),
      concatMap((action) =>
        this.service.updateMultipleAssetSettings(action.settings).pipe(
          map(() => UserProfileActions.UpdateMultipleAssetSettingSuccess({ id: action.id })),
          reportEffectError(UserProfileActions.UpdateMultipleAssetSettingFail),
        ),
      ),
    ),
  );

  setWorkspaceEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SetWorkspace),
      switchMap((action) =>
        this.service.setWorkspace(action.workspaceId).pipe(
          map((assetShortNames) =>
            UserProfileActions.SetWorkspaceSuccess({
              workspaceId: action.workspaceId,
              assetShortNames,
              redirectTo: action.redirectTo,
            }),
          ),
          catchError((error) => of(UserProfileActions.SetWorkspaceFail({ error }))),
        ),
      ),
    ),
  );

  setWorkspaceFailEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.SetWorkspaceFail),
        tap(() => this.toastService.error('Failed to apply workspace — please try again')),
      ),
    { dispatch: false },
  );

  /** A rejected set is the only signal a workspace was deleted elsewhere — silent, the toast above already reported it. */
  setWorkspaceFailRefreshEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SetWorkspaceFail),
      map(() => AppActions.RefreshWorkspaces({ silent: true })),
    ),
  );

  setWorkspaceSuccessEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.SetWorkspaceSuccess),
        tap((action) => recordRecentWorkspace(action.workspaceId)),
        map((action) =>
          this.router.navigate(wsLink(SemerisRoutes.asset.changeAsset), {
            queryParams: {
              redirectTo: action.redirectTo,
              assetShortNames: action.assetShortNames,
            },
          }),
        ),
      ),
    { dispatch: false },
  );

  resetPasswordEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.ResetPassword),
      switchMap((action) =>
        this.service.resetPassword().pipe(
          map(() => UserProfileActions.ResetPasswordSuccess()),
          reportEffectError(UserProfileActions.ResetPasswordFail),
        ),
      ),
    ),
  );

  resetPassworSuccessdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.ResetPasswordSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: 'Password reset initiated',
          config: 'success',
        }),
      ),
    ),
  );

  sendVerificationEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SendVerificationMail),
      exhaustMap(() =>
        this.service.verifyEmail().pipe(
          map(() => UserProfileActions.SendVerificationMailSuccess()),
          reportEffectError(UserProfileActions.SendVerificationMailFail),
        ),
      ),
    ),
  );

  sendVerificationSuccessdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SendVerificationMailSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: 'Verification mail sent',
          config: 'success',
        }),
      ),
    ),
  );

  sendVerificationFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SendVerificationMailFail),
      map(() =>
        AppActions.PushNotification({
          message: "Couldn't send the verification email. Try again in a moment.",
          config: 'fail',
        }),
      ),
    ),
  );

  /**
   * No backend event marks an email verified, so an unverified account re-checks with fresh tokens and reloads
   * once verified. A profile re-read that gets there first reloads too, so the app restarts verified.
   */
  reloadOnceVerified$: Observable<Action> = createEffect(() => {
    const unverified$ = this.permissionService.accountStatus$.pipe(
      map((status) => status === 'unverified'),
      distinctUntilChanged(),
    );

    return merge(
      unverified$.pipe(switchMap((unverified) => (unverified ? this.emailVerification.verified$() : EMPTY))),
      unverified$.pipe(
        pairwise(),
        filter(([wasUnverified, unverified]) => wasUnverified && !unverified),
      ),
    ).pipe(map(() => AppActions.GetNewTokenAndReload()));
  });

  updateSuccessdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateProfileSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: 'Profile updated',
          config: 'success',
        }),
      ),
    ),
  );

  updateSuccessdTokenRefreshEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateProfileSuccess),
      filter((action) => !!action.reloadApp),
      map(() => AppActions.GetNewTokenAndReload()),
    ),
  );

  triggerAppInitFailEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserProfileActions.LoadProfileSuccess),
        filter((action) => action.init && !action.selectedAssetShortName && action.init === 'welcome'),
        map(() => this.router.navigate(wsLink(SemerisRoutes.welcome))),
      ),
    { dispatch: false },
  );

  refreshColumnsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.RefreshAssetSettingSuccess),
      map((action) => action.assetSetting?.columns),
      withLatestFrom(this.store),
      filter(([columns, store]) => !!columns && !isEqual(columns, store.userProfile.columns)),
      map(([columns]) => UserProfileActions.SetColumns({ columns })),
    ),
  );

  updatePositionTableHiddenColumns$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionTableHiddenColumns),
      switchMap(({ columns }) =>
        this.service.updatePositionTableHiddenColumns(columns).pipe(
          map(() => UserProfileActions.UpdatePositionTableHiddenColumnsSuccess({ columns })),
          reportEffectError(UserProfileActions.UpdatePositionTableHiddenColumnsFail),
        ),
      ),
    ),
  );

  updatePositionTableHiddenColumnsFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionTableHiddenColumnsFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update table column visibility',
          config: 'fail',
        }),
      ),
    ),
  );

  updatePositionFiltersSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionFilters),
      switchMap(({ filters }) =>
        this.service.updatePositionFilters(filters).pipe(
          map(() => UserProfileActions.UpdatePositionFiltersSuccess({ filters })),
          reportEffectError(UserProfileActions.UpdatePositionFiltersFail),
        ),
      ),
    ),
  );

  updatePositionFiltersFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePositionFiltersFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update position filters',
          config: 'fail',
        }),
      ),
    ),
  );

  updateTransactionTaskSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateTransactionTaskSettings),
      switchMap(({ settings }) =>
        this.service.updateTransactionTaskSettings(settings).pipe(
          map(() => UserProfileActions.UpdateTransactionTaskSettingsSuccess({ settings })),
          reportEffectError(UserProfileActions.UpdateTransactionTaskSettingsFail),
        ),
      ),
    ),
  );

  updateTransactionTaskSettingsFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdateTransactionTaskSettingsFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update deal queue settings',
          config: 'fail',
        }),
      ),
    ),
  );

  updatePinnedTools$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePinnedTools),
      concatMap(({ pinnedTools, workspaceId }) =>
        this.service.updatePinnedTools(pinnedTools, workspaceId).pipe(
          map(() => UserProfileActions.UpdatePinnedToolsSuccess({ pinnedTools, workspaceId })),
          reportEffectError(UserProfileActions.UpdatePinnedToolsFail),
        ),
      ),
    ),
  );

  resetPinnedTools$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.ResetPinnedTools),
      concatMap(({ workspaceId }) =>
        this.service.updatePinnedTools(null, workspaceId).pipe(
          map(() => UserProfileActions.ResetPinnedToolsSuccess({ workspaceId })),
          reportEffectError(UserProfileActions.ResetPinnedToolsFail),
        ),
      ),
    ),
  );

  /**
   * A pin set that failed to save leaves the sidebar showing state the server never took — silently
   * reverting on the next reload. Both mutations share a message: a reset writes the defaults.
   */
  pinnedToolsFailEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.UpdatePinnedToolsFail, UserProfileActions.ResetPinnedToolsFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update your pinned tools',
          config: 'fail',
        }),
      ),
    ),
  );

  setFavoriteDatapointFieldIds$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.SetFavoriteDatapointFieldIds),
      concatMap(({ favoriteDatapointFieldIds }) =>
        this.service.updateUserSettings({ favoriteDatapointFieldIds }).pipe(
          map(() => UserProfileActions.SetFavoriteDatapointFieldIdsSuccess({ favoriteDatapointFieldIds })),
          reportEffectError(UserProfileActions.SetFavoriteDatapointFieldIdsFail),
        ),
      ),
    ),
  );
}
